import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  PaperPlaneTilt,
  ChartBar,
  Sparkle,
  Pulse as PulseIcon,
  CalendarDots,
  Trash,
  DotsThreeVertical,
  PencilSimple,
  SkipForward,
  X,
  TrendUp,
  TrendDown,
  WarningCircle,
} from '@phosphor-icons/react';

import { Sparkline } from '@/components/ui/sparkline';
import { DeltaChip } from '@/components/ui/delta-chip';

import {
  deletePulseSchedule,
  getPulseBenchmark,
  getPulseReport,
  getPulseTrend,
  getPulseRoleTrend,
  getPulseSummary,
  getPulseThread,
  launchPulseCampaign,
  listPulseCampaigns,
  listPulseSchedules,
  listPulseThreads,
  remindPulseCampaign,
  replyPulseThread,
  summarizePulseCampaign,
  updatePulseSchedule,
  type BenchmarkComparison,
  type CampaignReport,
  type CampaignTrend,
  type ENPSReport,
  type Heatmap,
  type PulseCampaign,
  type PulseRole,
  type PulseTrendScope,
  type PulseSchedule,
  type PulseThreadSummary,
} from '@/api/pulse';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { TablePagination } from '@/components/ui/table-pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useConfirm } from '@/components/ConfirmDialog';
import { ApiError } from '@/api/client';

// Pulse — the engagement-survey admin surface (docs/pulse-engagement-plan.md).
// Pulses run automatically on per-audience schedules (member / guardian /
// leader), so there is no manual "create a run" step here — this page manages
// those schedules and reads each run's anonymised, threshold-gated results +
// AI summary.
// Each survey audience gets its own brand-pastel pill so the three categories
// are instantly distinguishable across the schedules + results tables. Solid
// pastel background + ink text is theme-independent (ink stays readable on every
// pastel). Lime is deliberately NOT used — it's reserved for CTAs, so a badge
// never looks like a button.
const ROLE_TINT: Record<PulseRole, string> = {
  member: 'var(--aqua)',
  guardian: 'var(--pink)',
  leader: 'var(--orange)',
};

// The org-trend toggle adds an "all" pill for the whole-organisation view. Cream
// is the fourth brand pastel (kept off the audience badges, so "all" never reads
// as one specific audience) with the same ink text as the audience tints.
const TREND_SCOPE_TINT: Record<PulseTrendScope, string> = {
  all: 'var(--cream)',
  ...ROLE_TINT,
};

// LOW_RESPONSE_RATE — the share of invitees below which a run's participation is
// called out as needing attention rather than left as a plain number. 0.3 is a
// starting heuristic, not a measured threshold: tune it once we can see the real
// distribution across clubs. Kept as a single constant so that tuning is one
// edit, and so the number is visible rather than buried in a ternary.
const LOW_RESPONSE_RATE = 0.3;

// ResponseRate is the history table's "did anyone actually answer?" cell.
// Invite-side counts are exact at any size (they count who was asked and who
// answered, never what anyone answered) so — unlike every answer-side
// aggregate on this page — there is no privacy floor to respect here.
function ResponseRate({ invited, responded }: { invited: number; responded: number }) {
  const { t, i18n } = useTranslation('common');
  // A draft that was never launched has no invites. "0%" would read as a
  // failure that hasn't had the chance to happen yet, so show nothing measured.
  if (invited === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  const rate = responded / invited;
  const low = rate < LOW_RESPONSE_RATE;
  const pct = new Intl.NumberFormat(i18n.language || undefined, {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(rate);
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className="tabular-nums">
        {responded} / {invited}
      </span>
      <span
        className={
          'inline-flex items-center gap-1 tabular-nums ' +
          (low ? 'font-semibold text-destructive' : 'text-muted-foreground')
        }
      >
        {/* The icon carries the warning too — colour alone must never be the
            only signal (WCAG 1.4.1). */}
        {low && <WarningCircle size={14} weight="fill" aria-hidden />}
        {pct}
        {low && <span className="sr-only">{t('pulse.list.lowResponse')}</span>}
      </span>
    </span>
  );
}

function RoleBadge({ role }: { role: PulseRole }) {
  const { t } = useTranslation('common');
  return (
    <span
      className="inline-flex items-center rounded-pill px-2.5 py-0.5 font-heading text-xs font-semibold"
      style={{ backgroundColor: ROLE_TINT[role], color: 'var(--ink)' }}
    >
      {t(`pulse.create.role.${role}`)}
    </span>
  );
}

export function Pulse() {
  const { t, i18n } = useTranslation('common');
  const dateFmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });
  // The window a run covers, so an admin can tell otherwise-identical rows apart
  // in the history table. Prefer the opens–closes window; fall back to whichever
  // single date exists (a draft has neither yet).
  const campaignDates = (c: PulseCampaign): string => {
    if (c.opensAt && c.closesAt) {
      return `${dateFmt.format(new Date(c.opensAt))} – ${dateFmt.format(new Date(c.closesAt))}`;
    }
    const single = c.closesAt ?? c.opensAt ?? c.createdAt;
    return single ? dateFmt.format(new Date(single)) : '—';
  };
  const { slug = '' } = useParams();
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const qc = useQueryClient();
  const confirm = useConfirm();

  const [resultsCampaign, setResultsCampaign] = useState<PulseCampaign | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const campaignsQuery = useQuery({
    queryKey: ['pulse-campaigns', slug],
    queryFn: () => listPulseCampaigns(token, slug),
    enabled: !!slug,
  });

  const launchM = useMutation({
    mutationFn: (campaignId: string) =>
      launchPulseCampaign(token, slug, campaignId),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['pulse-campaigns', slug] }),
  });

  const onLaunch = async (c: PulseCampaign) => {
    const ok = await confirm({
      title: t('pulse.launch.confirmTitle'),
      body: t('pulse.launch.confirmBody'),
      confirmLabel: t('pulse.actions.launch'),
    });
    if (ok) launchM.mutate(c.id);
  };

  const remindM = useMutation({
    mutationFn: (campaignId: string) => remindPulseCampaign(token, slug, campaignId),
  });

  const onRemind = async (c: PulseCampaign) => {
    const ok = await confirm({
      title: t('pulse.remind.confirmTitle'),
      body: t('pulse.remind.confirmBody'),
      confirmLabel: t('pulse.actions.remind'),
    });
    if (ok) remindM.mutate(c.id);
  };

  const campaigns = campaignsQuery.data?.campaigns ?? [];

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-4 md:p-8">
      <header className="flex flex-col gap-1">
        <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold tracking-tight md:text-3xl">
          <PulseIcon size={28} weight="bold" aria-hidden />
          {t('pulse.title')}
        </h1>
        <p className="max-w-2xl text-sm text-muted-foreground md:text-base">
          {t('pulse.subtitle')}
        </p>
      </header>

      <Tabs defaultValue="results" className="flex flex-col gap-4">
        <TabsList className="self-start">
          <TabsTrigger value="scheduled">{t('pulse.tabs.scheduled')}</TabsTrigger>
          <TabsTrigger value="results">{t('pulse.tabs.results')}</TabsTrigger>
        </TabsList>

        {/* Recurring auto-send schedules — pulses run automatically; there is no
            manual "create a run" step. */}
        <TabsContent value="scheduled">
          <SchedulesSection slug={slug} token={token} />
        </TabsContent>

        {/* Run history — a paginated table in a card matching the schedule tab,
            preceded by the org-wide over-time overview. */}
        <TabsContent value="results" className="flex flex-col gap-4">
          <OrgTrendOverview slug={slug} token={token} />
          <div className="flex flex-col gap-4 rounded-xl border border-border p-4">
            <div className="flex items-center gap-2">
              <ChartBar size={18} weight="bold" aria-hidden />
              <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">
                {t('pulse.list.heading')}
              </h2>
            </div>
            {campaignsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">{t('pulse.list.loading')}</p>
            ) : campaigns.length === 0 ? (
              <p className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                {t('pulse.list.empty')}
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead>{t('pulse.list.colAudience')}</TableHead>
                        <TableHead>{t('pulse.list.colDates')}</TableHead>
                        {/* Reuses the report drawer's label — same quantity, so
                            a second key would only risk the two drifting. */}
                        <TableHead>{t('pulse.report.participationLabel')}</TableHead>
                        <TableHead>{t('pulse.list.colStatus')}</TableHead>
                        <TableHead className="text-right" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {campaigns.slice((page - 1) * pageSize, page * pageSize).map((c) => (
                        <TableRow key={c.id}>
                          <TableCell>
                            <RoleBadge role={c.targetRole} />
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm text-muted-foreground tabular-nums">
                            {campaignDates(c)}
                          </TableCell>
                          <TableCell className="text-sm">
                            <ResponseRate invited={c.invited} responded={c.responded} />
                          </TableCell>
                          <TableCell>
                            <Badge variant={c.status === 'open' ? 'default' : 'secondary'}>
                              {t(`pulse.status.${c.status}`)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {c.status === 'draft' ? (
                                <Button
                                  size="sm"
                                  onClick={() => void onLaunch(c)}
                                  disabled={launchM.isPending}
                                >
                                  <PaperPlaneTilt size={16} weight="bold" aria-hidden />
                                  {t('pulse.actions.launch')}
                                </Button>
                              ) : (
                                <>
                                  {c.status === 'open' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => void onRemind(c)}
                                      disabled={remindM.isPending}
                                    >
                                      <PaperPlaneTilt size={16} weight="bold" aria-hidden />
                                      {t('pulse.actions.remind')}
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setResultsCampaign(c)}
                                  >
                                    <ChartBar size={16} weight="bold" aria-hidden />
                                    {t('pulse.actions.viewReport')}
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <TablePagination
                  total={campaigns.length}
                  page={page}
                  size={pageSize}
                  onPageChange={setPage}
                  onSizeChange={(s) => {
                    setPageSize(s);
                    setPage(1);
                  }}
                />
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <ResultsDialog
        slug={slug}
        token={token}
        campaign={resultsCampaign}
        onClose={() => setResultsCampaign(null)}
      />
    </div>
  );
}

// ResultsDialog shows one run's results in a modal: a "Resultat" tab (the gated
// report + AI summary) and an "Uppföljningar" tab (anonymous follow-up threads).
// Opens whenever `campaign` is set.
function ResultsDialog({
  slug,
  token,
  campaign,
  onClose,
}: {
  slug: string;
  token: string | null;
  campaign: PulseCampaign | null;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');
  const dialogRef = useRef<HTMLDialogElement>(null);
  // The mounted <dialog> element, tracked in state so children can portal their
  // dropdowns INTO it. showModal() puts the dialog in the browser top-layer;
  // anything Radix portals to document.body sits below it and is unclickable.
  const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (campaign && !el.open) el.showModal();
    else if (!campaign && el.open) el.close();
    setContainerEl(campaign ? el : null);
  }, [campaign]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const handleClose = () => onClose();
    el.addEventListener('close', handleClose);
    return () => el.removeEventListener('close', handleClose);
  }, [onClose]);

  if (!campaign) {
    return <dialog ref={dialogRef} className="hidden" aria-hidden />;
  }

  return (
    <dialog
      ref={dialogRef}
      className="rounded-2xl border border-border bg-background p-0 shadow-2xl backdrop:bg-foreground/40"
    >
      <div className="flex max-h-[86vh] w-[min(56rem,94vw)] flex-col gap-4 p-6">
        <header className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Badge variant={campaign.status === 'open' ? 'default' : 'secondary'}>
              {t(`pulse.status.${campaign.status}`)}
            </Badge>
            <h2 className="font-heading text-xl font-bold text-foreground">
              {t(`pulse.create.role.${campaign.targetRole}`)}
            </h2>
          </div>
          <button
            type="button"
            aria-label={t('confirmDialog.cancel')}
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-pill text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X size={20} weight="bold" aria-hidden />
          </button>
        </header>

        <Tabs defaultValue="results" className="flex min-h-0 flex-1 flex-col">
          <TabsList>
            <TabsTrigger value="results">{t('pulse.results.tabResults')}</TabsTrigger>
            <TabsTrigger value="followups">{t('pulse.actions.followups')}</TabsTrigger>
          </TabsList>
          <div className="min-h-0 flex-1 overflow-y-auto pr-3">
            <TabsContent value="results">
              <ReportPanel
                slug={slug}
                token={token}
                campaignId={campaign.id}
                role={campaign.targetRole}
                portalContainer={containerEl}
              />
            </TabsContent>
            <TabsContent value="followups">
              <p className="mt-3 rounded-lg bg-muted p-2 text-xs text-muted-foreground">
                {t('pulse.results.followupsHelp')}
              </p>
              <FollowupsPanel slug={slug} token={token} campaignId={campaign.id} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </dialog>
  );
}

// EnpsHeadline is the report's hero number: the org-wide eNPS shown large, with
// a trend pill comparing it to the previous run of the same role. Gated like
// every other cell — below the response floor it reads "not enough responses
// yet" rather than inventing a score.
function EnpsHeadline({ enps }: { enps: ENPSReport }) {
  const { t } = useTranslation('common');

  if (enps.suppressed || enps.score == null) {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="font-heading text-xs font-bold uppercase tracking-wider text-muted-foreground">
          {t('pulse.report.enps')}
        </span>
        <span className="text-sm text-muted-foreground">{t('pulse.report.suppressed')}</span>
      </div>
    );
  }

  const score = Math.round(enps.score);
  const delta = enps.prior != null ? Math.round(enps.score - enps.prior) : null;
  const up = delta != null && delta > 0;
  const down = delta != null && delta < 0;
  const pillStyle: CSSProperties = up
    ? { backgroundColor: 'color-mix(in srgb, var(--lime) 32%, transparent)', color: 'var(--ink)' }
    : down
      ? { backgroundColor: 'color-mix(in srgb, var(--color-error) 15%, transparent)', color: 'var(--color-error)' }
      : { color: 'var(--muted-foreground)' };

  return (
    <div className="flex flex-col gap-1">
      <span className="font-heading text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {t('pulse.report.enps')}
      </span>
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-heading text-5xl font-extrabold tracking-tight tabular-nums">
          {score > 0 ? '+' : ''}
          {score}
        </span>
        {delta != null && (
          <span
            className="inline-flex items-center gap-1 rounded-pill px-2.5 py-1 text-sm font-semibold tabular-nums"
            style={pillStyle}
          >
            {up && <TrendUp size={16} weight="bold" />}
            {down && <TrendDown size={16} weight="bold" />}
            {delta > 0 ? '+' : ''}
            {delta}
          </span>
        )}
      </div>
      {delta != null && (
        <span className="text-xs text-muted-foreground">{t('pulse.report.enpsVsPrev')}</span>
      )}
    </div>
  );
}

// periodDelta finds the change between the newest present point and the present
// point nearest `days` before it (within a half-interval tolerance so a biweekly
// cadence still matches a "1 month ago" bucket). Returns null when no comparable
// earlier point exists — the caller then omits that period's chip rather than
// inventing a baseline.
function periodDelta(
  series: { at: number; v: number | null }[],
  days: number,
): number | null {
  const present = series.filter((p) => p.v != null) as { at: number; v: number }[];
  if (present.length < 2) return null;
  const newest = present[present.length - 1]!;
  const target = newest.at - days * 86_400_000;
  const tol = days * 86_400_000 * 0.6;
  let best: { at: number; v: number } | null = null;
  for (const p of present) {
    if (p === newest) continue;
    if (Math.abs(p.at - target) > tol) continue;
    if (!best || Math.abs(p.at - target) < Math.abs(best.at - target)) best = p;
  }
  return best ? newest.v - best.v : null;
}

// TrendStrip shows how the role's own eNPS + drivers have moved across its
// recent runs, in a single run's report. Suppressed runs are gaps, never
// invented values.
function TrendStrip({
  slug,
  token,
  campaignId,
}: {
  slug: string;
  token: string | null;
  campaignId: string;
}) {
  const query = useQuery({
    queryKey: ['pulse-trend', slug, campaignId],
    queryFn: () => getPulseTrend(token, slug, campaignId),
  });
  if (!query.data) return null;
  return <TrendView trend={query.data} />;
}

// TrendView renders a role's eNPS + driver movement over time — a sparkline per
// metric with the latest value and the change vs ~1 month / 6 months / a year
// ago. Every point is gated like the single-run report; a run below the response
// floor is a gap, never an invented number. Shared by the in-report TrendStrip
// and the page-level OrgTrendOverview.
function TrendView({ trend }: { trend: CampaignTrend }) {
  const { t, i18n } = useTranslation('common');
  if (trend.points.length < 2) return null;

  const times = trend.points.map((p) => new Date(p.at).getTime());
  const dateFmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short' });

  // eNPS series across the runs (nulls where a run was below the floor).
  const enpsSeries = trend.points.map((p, i) => ({ at: times[i]!, v: p.enps ?? null }));
  const enpsPresent = enpsSeries.filter((p): p is { at: number; v: number } => p.v != null);

  // Distinct drivers seen in the window, in the order the latest run lists them.
  const driverOrder: string[] = [];
  for (const p of trend.points) {
    for (const d of p.drivers) if (!driverOrder.includes(d.driver)) driverOrder.push(d.driver);
  }
  const driverSeries = driverOrder.map((driver) => {
    const series = trend.points.map((p, i) => {
      const cell = p.drivers.find((d) => d.driver === driver);
      return { at: times[i]!, v: cell && !cell.suppressed ? (cell.mean ?? null) : null };
    });
    return { driver, series };
  });

  const periods: { key: string; days: number }[] = [
    { key: 'm1', days: 30 },
    { key: 'm6', days: 182 },
    { key: 'y1', days: 365 },
  ];

  // Nothing plottable at all (every metric a single point or all gaps).
  const anyEnps = enpsPresent.length >= 2;
  const anyDriver = driverSeries.some((d) => d.series.filter((p) => p.v != null).length >= 2);
  if (!anyEnps && !anyDriver) return null;

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border bg-muted/30 p-3">
      <div className="flex items-baseline justify-between">
        <span className="font-heading text-xs font-bold uppercase tracking-wider text-muted-foreground">
          {t('pulse.report.trend.heading')}
        </span>
        <span className="text-xs text-muted-foreground tabular-nums">
          {dateFmt.format(times[0]!)} – {dateFmt.format(times[times.length - 1]!)}
        </span>
      </div>

      {anyEnps && (
        <TrendRow
          label={t('pulse.report.enps')}
          series={enpsSeries}
          latest={enpsPresent[enpsPresent.length - 1]!.v}
          periods={periods}
          digits={0}
          signedLatest
        />
      )}
      {driverSeries.map(({ driver, series }) => {
        const present = series.filter((p): p is { at: number; v: number } => p.v != null);
        if (present.length < 2) return null;
        return (
          <TrendRow
            key={driver}
            label={t(`pulse.drivers.${driver}`, { defaultValue: driver })}
            series={series}
            latest={present[present.length - 1]!.v}
            periods={periods}
            digits={1}
          />
        );
      })}
      <span className="text-[11px] text-muted-foreground">{t('pulse.report.trend.note')}</span>
    </div>
  );
}

// OrgTrendOverview sits at the top of the results page: how the whole
// organisation has developed over time on each metric. Defaults to "all" — every
// audience's runs on one timeline — and can be narrowed to a single audience
// (members / guardians / leaders) to see that role's eNPS + drivers across every
// run. Same gating as everywhere else — a run below the response floor is a gap,
// never a guessed number.
function OrgTrendOverview({ slug, token }: { slug: string; token: string | null }) {
  const { t } = useTranslation('common');
  // Default to the whole-organisation view — the "how are we doing overall?"
  // question is the one an admin opens this page to answer; drill into a single
  // audience from there.
  const [role, setRole] = useState<PulseTrendScope>('all');
  const query = useQuery({
    queryKey: ['pulse-role-trend', slug, role],
    queryFn: () => getPulseRoleTrend(token, slug, role),
  });
  const roles: PulseTrendScope[] = ['all', 'member', 'guardian', 'leader'];
  const trend = query.data;
  const hasData = !!trend && trend.points.length >= 2;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <TrendUp size={18} weight="bold" aria-hidden />
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">
            {t('pulse.orgTrend.heading')}
          </h2>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {roles.map((rr) => {
            const active = rr === role;
            return (
              <button
                key={rr}
                type="button"
                onClick={() => setRole(rr)}
                className={
                  'rounded-pill px-3 py-1 font-heading text-xs font-semibold transition-colors ' +
                  (active ? '' : 'border border-border text-muted-foreground hover:bg-muted')
                }
                style={
                  active
                    ? { backgroundColor: TREND_SCOPE_TINT[rr], color: 'var(--ink)' }
                    : undefined
                }
                aria-pressed={active}
              >
                {rr === 'all' ? t('pulse.orgTrend.scopeAll') : t(`pulse.create.role.${rr}`)}
              </button>
            );
          })}
        </div>
      </div>
      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">{t('pulse.report.loading')}</p>
      ) : hasData ? (
        <TrendView trend={trend} />
      ) : (
        <p className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
          {t('pulse.orgTrend.empty')}
        </p>
      )}
    </div>
  );
}

// TrendRow is one metric's line in the trend strip: label, sparkline, the latest
// value, and a chip per comparison period that has a comparable earlier run.
function TrendRow({
  label,
  series,
  latest,
  periods,
  digits,
  signedLatest = false,
}: {
  label: string;
  series: { at: number; v: number | null }[];
  latest: number;
  periods: { key: string; days: number }[];
  digits: number;
  signedLatest?: boolean;
}) {
  const { t } = useTranslation('common');
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="w-44 shrink-0 text-sm leading-tight">{label}</span>
      {/* fluid: the chart claims the row's leftover width so a wide card shows
          real resolution instead of a 132px stub with dead space beside it.
          The value + delta chips keep their intrinsic widths and stay
          right-aligned. */}
      <Sparkline points={series} fluid />
      <span className="w-12 shrink-0 text-right text-sm font-semibold tabular-nums">
        {signedLatest && latest > 0 ? '+' : ''}
        {latest.toFixed(digits)}
      </span>
      <div className="flex flex-wrap items-center gap-1">
        {periods.map((per) => {
          const d = periodDelta(series, per.days);
          if (d == null) return null;
          return (
            <span key={per.key} className="inline-flex items-center gap-1">
              <span className="text-[11px] text-muted-foreground">
                {t(`pulse.report.trend.period.${per.key}`)}
              </span>
              <DeltaChip delta={d} digits={digits} />
            </span>
          );
        })}
      </div>
    </div>
  );
}

// BenchmarkStrip compares this run to OTHER clubs — all clubs and same-sport
// clubs — per metric. Every peer number is an average over ≥ floor clubs (the
// backend withholds a thinner cohort), so no single club is ever exposed. The
// club's own value comes from the report it's paired with. Renders nothing when
// the platform kill-switch is off or no cohort has data yet.
function BenchmarkStrip({
  slug,
  token,
  campaignId,
  report,
}: {
  slug: string;
  token: string | null;
  campaignId: string;
  report: CampaignReport;
}) {
  const { t } = useTranslation('common');
  const query = useQuery({
    queryKey: ['pulse-benchmark', slug, campaignId],
    queryFn: () => getPulseBenchmark(token, slug, campaignId),
  });

  const bm: BenchmarkComparison | undefined = query.data;
  if (!bm || !bm.enabled || bm.cohorts.length === 0) return null;

  // The club's own gated value for a metric ('enps' or a driver key), or null
  // when this run suppressed it.
  const ownValue = (metric: string): number | null => {
    if (metric === 'enps') return report.enps.suppressed ? null : (report.enps.score ?? null);
    const d = report.drivers.find((dr) => dr.driver === metric);
    return d && !d.suppressed ? (d.mean ?? null) : null;
  };
  const label = (metric: string) =>
    metric === 'enps'
      ? t('pulse.report.enps')
      : t(`pulse.drivers.${metric}`, { defaultValue: metric });
  const digits = (metric: string) => (metric === 'enps' ? 0 : 1);

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border bg-muted/30 p-3">
      <span className="font-heading text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {t('pulse.report.benchmark.heading')}
      </span>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[26rem] text-sm">
          <thead>
            <tr className="text-xs text-muted-foreground">
              <th className="pb-1 text-left font-medium" />
              <th className="pb-1 text-right font-medium">{t('pulse.report.benchmark.you')}</th>
              <th className="pb-1 text-right font-medium">{t('pulse.report.benchmark.allClubs')}</th>
              <th className="pb-1 text-right font-medium">{t('pulse.report.benchmark.sameSport')}</th>
            </tr>
          </thead>
          <tbody>
            {bm.cohorts.map((c) => {
              const own = ownValue(c.metric);
              const d = digits(c.metric);
              return (
                <tr key={c.metric} className="border-t border-border/60">
                  <td className="py-1.5 pr-2">{label(c.metric)}</td>
                  <td className="py-1.5 text-right font-semibold tabular-nums">
                    {own != null ? (c.metric === 'enps' && own > 0 ? '+' : '') + own.toFixed(d) : '—'}
                  </td>
                  <BenchmarkCell peer={c.all} own={own} digits={d} enps={c.metric === 'enps'} />
                  <BenchmarkCell peer={c.sport} own={own} digits={d} enps={c.metric === 'enps'} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <span className="text-[11px] text-muted-foreground">{t('pulse.report.benchmark.note')}</span>
    </div>
  );
}

// BenchmarkCell renders one peer-average cell: the cohort average plus a chip
// with this club's gap to it (up = above peers). A withheld (sub-floor) cohort
// reads "not enough clubs yet".
function BenchmarkCell({
  peer,
  own,
  digits,
  enps,
}: {
  peer: number | undefined;
  own: number | null;
  digits: number;
  enps: boolean;
}) {
  const { t } = useTranslation('common');
  if (peer == null) {
    return (
      <td className="py-1.5 text-right text-xs text-muted-foreground">
        {t('pulse.report.benchmark.fewClubs')}
      </td>
    );
  }
  return (
    <td className="py-1.5 text-right tabular-nums">
      <div className="flex items-center justify-end gap-1.5">
        <span>
          {enps && peer > 0 ? '+' : ''}
          {peer.toFixed(digits)}
        </span>
        {own != null && <DeltaChip delta={own - peer} digits={digits} />}
      </div>
    </td>
  );
}

// ReportPanel renders one run's anonymised, threshold-gated results:
// participation, the eNPS headline, and each driver's mean. Every score obeys
// the minimum-response floor — a suppressed cell reads "not enough responses
// yet", never a guessed number.
function ReportPanel({
  slug,
  token,
  campaignId,
  role,
  portalContainer,
}: {
  slug: string;
  token: string | null;
  campaignId: string;
  role: PulseRole;
  portalContainer?: HTMLElement | null;
}) {
  const { t } = useTranslation('common');
  const query = useQuery({
    queryKey: ['pulse-report', slug, campaignId],
    queryFn: () => getPulseReport(token, slug, campaignId),
  });

  if (query.isLoading || !query.data) {
    return (
      <p className="mt-3 border-t border-border pt-3 text-sm text-muted-foreground">
        {t('pulse.report.loading')}
      </p>
    );
  }
  const r = query.data;
  const pct =
    r.participation.Invited > 0
      ? Math.round((r.participation.Responded / r.participation.Invited) * 100)
      : 0;
  const ageSlices = r.slices.filter((s) => s.dimension === 'age_band');
  const genderSlices = r.slices.filter((s) => s.dimension === 'gender');

  // With many teams the full per-team grids become unreadable, so above a
  // threshold we swap them for the Best & worst highlights and trim the
  // participation list to the teams that most need a nudge.
  const teamCount = Math.max(r.teams.length, r.heatmap.rows.length);
  const manyTeams = teamCount > 10;
  const participationTeams = r.participationByTeam.filter((tp) => tp.teamLabel);
  const rate = (tp: { invited: number; responded: number }) =>
    tp.invited > 0 ? tp.responded / tp.invited : 0;
  const shownParticipation = manyTeams
    ? [...participationTeams].sort((a, b) => rate(a) - rate(b)).slice(0, 8)
    : participationTeams;

  return (
    <div className="mt-3 flex flex-col gap-4 border-t border-border pt-3">
      {/* eNPS headline — the org-wide "how are we feeling" number, up top and
          big, with a trend arrow vs the previous run of the same role. */}
      <EnpsHeadline enps={r.enps} />

      {/* How the club's own eNPS + drivers have moved over its recent runs. */}
      <TrendStrip slug={slug} token={token} campaignId={campaignId} />

      {/* How this run compares to other clubs (all + same sport), each cohort
          gated by the ≥N-club floor so no single club is ever exposed. */}
      <BenchmarkStrip slug={slug} token={token} campaignId={campaignId} report={r} />

      {/* Participation */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">{t('pulse.report.participationLabel')}</span>
          <span className="text-muted-foreground">
            {t('pulse.report.participation', {
              responded: r.participation.Responded,
              invited: r.participation.Invited,
            })}
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-pill bg-muted">
          <div className="h-full rounded-pill bg-primary" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Per-team participation — who's answered, per team, so the admin can
          nudge the laggards. Identity-side counts, safe to show unrounded. */}
      {participationTeams.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="font-heading text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {manyTeams
              ? t('pulse.report.participationLowest')
              : t('pulse.report.participationByTeam')}
          </span>
          <ul className="flex flex-col gap-1">
            {shownParticipation.map((tp) => {
              const teamPct =
                tp.invited > 0 ? Math.round((tp.responded / tp.invited) * 100) : 0;
              return (
                <li key={tp.teamId ?? tp.teamLabel} className="flex items-center gap-3 text-sm">
                  <span className="w-28 shrink-0 truncate">{tp.teamLabel}</span>
                  <span className="h-2 flex-1 overflow-hidden rounded-pill bg-muted">
                    <span
                      className="block h-full rounded-pill bg-primary"
                      style={{ width: `${teamPct}%` }}
                    />
                  </span>
                  <span className="w-16 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                    {tp.responded}/{tp.invited}
                  </span>
                </li>
              );
            })}
          </ul>
          {manyTeams && (
            <span className="text-xs text-muted-foreground">
              {t('pulse.report.ofTeams', { shown: shownParticipation.length, total: participationTeams.length })}
            </span>
          )}
        </div>
      )}

      {/* AI theme summary of the free-text comments. */}
      <SummaryPanel slug={slug} token={token} campaignId={campaignId} />

      {/* Drivers */}
      <ReportBars
        heading={t('pulse.report.drivers')}
        rows={r.drivers.map((d) => ({
          key: d.driver,
          label: t(`pulse.drivers.${d.driver}`, { defaultValue: d.driver }),
          mean: d.mean,
          suppressed: d.suppressed,
        }))}
        suppressedLabel={t('pulse.report.suppressed')}
      />

      {/* Best & worst highlights — always shown; the only team breakdown once a
          club has too many teams for the full grid. */}
      <BestWorst report={r} />

      {/* Per-team drill-down — look up any single team's participation + scores. */}
      <TeamDetail report={r} portalContainer={portalContainer ?? null} />

      {/* Full team × driver heatmap + per-team list — only for clubs with few
          enough teams to read; larger clubs rely on Best & worst above. */}
      {!manyTeams && r.heatmap.rows.length > 0 && r.heatmap.drivers.length > 0 && (
        <HeatmapGrid heatmap={r.heatmap} />
      )}

      {!manyTeams && r.teams.length > 0 && (
        <ReportBars
          heading={t('pulse.report.teams')}
          rows={r.teams.map((tm) => ({
            key: tm.teamLabel,
            label: tm.teamLabel,
            mean: tm.mean,
            suppressed: tm.suppressed,
          }))}
          suppressedLabel={t('pulse.report.suppressed')}
        />
      )}

      {/* Drill-down for big clubs: everything per team, one click away, so an
          admin can look up how a specific team scored (e.g. when a leader asks). */}
      {manyTeams && (
        <details className="rounded-xl border border-border p-3">
          <summary className="cursor-pointer text-sm font-medium">
            {t('pulse.report.allTeams', { count: teamCount })}
          </summary>
          <div className="mt-3 flex flex-col gap-4">
            {participationTeams.length > 0 && (
              <ReportBars
                heading={t('pulse.report.participationByTeam')}
                rows={participationTeams.map((tp) => ({
                  key: tp.teamId ?? tp.teamLabel,
                  label: tp.teamLabel,
                  mean: tp.invited > 0 ? (tp.responded / tp.invited) * 5 : 0,
                  suppressed: false,
                  note: `${tp.responded}/${tp.invited}`,
                }))}
                suppressedLabel={t('pulse.report.suppressed')}
              />
            )}
            {r.teams.length > 0 && (
              <ReportBars
                heading={t('pulse.report.teams')}
                rows={r.teams.map((tm) => ({
                  key: tm.teamLabel,
                  label: tm.teamLabel,
                  mean: tm.mean,
                  suppressed: tm.suppressed,
                }))}
                suppressedLabel={t('pulse.report.suppressed')}
              />
            )}
            {r.heatmap.rows.length > 0 && r.heatmap.drivers.length > 0 && (
              <HeatmapGrid heatmap={r.heatmap} />
            )}
          </div>
        </details>
      )}

      {/* Sections — the roll-up unit for leaders, and an extra lens for others. */}
      {r.sections.length > 0 && (
        <ReportBars
          heading={t('pulse.report.sections')}
          rows={r.sections.map((s) => ({
            key: s.sectionId ?? s.sectionLabel,
            label: s.sectionLabel || t('pulse.report.noSection'),
            mean: s.mean,
            suppressed: s.suppressed,
          }))}
          suppressedLabel={t('pulse.report.suppressed')}
        />
      )}

      {/* Age slices */}
      {ageSlices.length > 0 && (
        <ReportBars
          heading={t('pulse.report.byAge')}
          rows={ageSlices.map((s) => ({
            key: s.value,
            label: s.value,
            mean: s.mean,
            suppressed: s.suppressed,
          }))}
          suppressedLabel={t('pulse.report.suppressed')}
        />
      )}

      {/* Gender slices */}
      {genderSlices.length > 0 && (
        <ReportBars
          heading={t('pulse.report.byGender')}
          rows={genderSlices.map((s) => ({
            key: s.value,
            label: t(`pulse.gender.${s.value}`, { defaultValue: s.value }),
            mean: s.mean,
            suppressed: s.suppressed,
          }))}
          suppressedLabel={t('pulse.report.suppressed')}
        />
      )}

      {/* Leader pulses aggregate org-wide only — teams usually have one or two
          leaders, so a per-team breakdown would expose them. */}
      {role === 'leader' && (
        <p className="rounded-lg bg-muted p-2 text-xs text-muted-foreground">
          {t('pulse.report.leaderNote')}
        </p>
      )}
    </div>
  );
}

// BestWorst surfaces the standout teams so a club with many teams doesn't have
// to scan a 200-row grid: the top/bottom teams by overall score, and the best +
// worst team for each engagement area. Derived purely from the report's gated
// team + heatmap data (suppressed cells are skipped, never guessed).
function BestWorst({ report }: { report: CampaignReport }) {
  const { t } = useTranslation('common');
  const scored = report.teams
    .filter((tm) => !tm.suppressed && tm.mean != null)
    .sort((a, b) => b.mean! - a.mean!);

  // Per-area (driver) best + worst, from the heatmap columns.
  const perArea = report.heatmap.drivers
    .map((driver, di) => {
      const cells = report.heatmap.rows
        .map((row) => ({ label: row.unitLabel, mean: row.cells[di]?.mean, sup: row.cells[di]?.suppressed }))
        .filter((c) => !c.sup && c.mean != null)
        .sort((a, b) => b.mean! - a.mean!);
      if (cells.length < 2) return null;
      return { driver, best: cells[0]!, worst: cells[cells.length - 1]! };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  if (scored.length < 2 && perArea.length === 0) return null;

  // Show up to 5 each, but never overlap when there are few teams.
  const k = Math.min(5, Math.max(1, Math.floor(scored.length / 2)));
  const best = scored.slice(0, k);
  const worst = scored.slice(-k).reverse();

  const teamList = (rows: typeof scored) => (
    <ul className="flex flex-col gap-1">
      {rows.map((tm) => (
        <li key={tm.teamId ?? tm.teamLabel} className="flex items-center justify-between gap-3 text-sm">
          <span className="truncate">{tm.teamLabel}</span>
          <span className="shrink-0 font-medium tabular-nums">{tm.mean!.toFixed(1)}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-muted/30 p-3">
      <span className="font-heading text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {t('pulse.report.bestWorst')}
      </span>
      {scored.length >= 2 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-foreground/70">
              {t('pulse.report.bestOverall')}
            </span>
            {teamList(best)}
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-foreground/70">
              {t('pulse.report.worstOverall')}
            </span>
            {teamList(worst)}
          </div>
        </div>
      )}
      {perArea.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-foreground/70">
            {t('pulse.report.byAreaBestWorst')}
          </span>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('pulse.report.area')}</TableHead>
                  <TableHead>{t('pulse.report.best')}</TableHead>
                  <TableHead>{t('pulse.report.worst')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {perArea.map((a) => (
                  <TableRow key={a.driver}>
                    <TableCell className="font-medium">
                      {t(`pulse.drivers.${a.driver}`, { defaultValue: a.driver })}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {a.best.label}{' '}
                      <span className="text-muted-foreground tabular-nums">{a.best.mean!.toFixed(1)}</span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {a.worst.label}{' '}
                      <span className="text-muted-foreground tabular-nums">{a.worst.mean!.toFixed(1)}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}

// TeamDetail is the per-team drill-down: pick any team (works at any club size —
// a leader asks "how did T-15 do?") and see that team's participation + area
// scores. Participation is always shown (identity-side counts). Scores come
// straight from the already-gated report data, so a team below the response
// floor shows "not enough responses yet" — the anonymity floor is never relaxed.
function TeamDetail({
  report,
  portalContainer,
}: {
  report: CampaignReport;
  portalContainer?: HTMLElement | null;
}) {
  const { t } = useTranslation('common');
  const [teamKey, setTeamKey] = useState('');
  const teams = report.participationByTeam.filter((tp) => tp.teamLabel);
  // Only meaningful for team-unit runs (member/guardian); leaders roll up to
  // sections and are never shown per team.
  if (teams.length === 0 || report.heatmap.unit !== 'team') return null;

  const matches = (id: string | undefined, label: string) => (t2: {
    teamId?: string;
    teamLabel: string;
  }) => (t2.teamId && id ? t2.teamId === id : t2.teamLabel === label);

  const selected = teams.find((tp) => (tp.teamId ?? tp.teamLabel) === teamKey) ?? null;
  const overall = selected
    ? report.teams.find((tm) => matches(selected.teamId, selected.teamLabel)(tm))
    : undefined;
  const hmRow = selected
    ? report.heatmap.rows.find(
        (row) =>
          (row.unitId && selected.teamId ? row.unitId === selected.teamId : row.unitLabel === selected.teamLabel),
      )
    : undefined;

  return (
    <div className="flex flex-col gap-2">
      <span className="font-heading text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {t('pulse.report.teamDetail')}
      </span>
      <Select value={teamKey} onValueChange={setTeamKey}>
        <SelectTrigger className="w-full sm:w-64">
          <SelectValue placeholder={t('pulse.report.pickTeam')} />
        </SelectTrigger>
        <SelectContent container={portalContainer ?? null}>
          {teams.map((tp) => (
            <SelectItem key={tp.teamId ?? tp.teamLabel} value={tp.teamId ?? tp.teamLabel}>
              {tp.teamLabel}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {selected && (
        <div className="flex flex-col gap-3 rounded-xl border border-border p-3">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">{t('pulse.report.participationLabel')}</span>
            <span className="text-muted-foreground tabular-nums">
              {t('pulse.report.participation', {
                responded: selected.responded,
                invited: selected.invited,
              })}
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-2 text-sm">
            <span className="font-medium">{t('pulse.report.teamOverall')}</span>
            {overall && !overall.suppressed && overall.mean != null ? (
              <span className="font-heading text-lg font-bold tabular-nums">
                {overall.mean.toFixed(1)}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">{t('pulse.report.suppressed')}</span>
            )}
          </div>
          {hmRow && hmRow.cells.length > 0 && (
            <ReportBars
              heading={t('pulse.report.drivers')}
              rows={hmRow.cells.map((c) => ({
                key: c.driver,
                label: t(`pulse.drivers.${c.driver}`, { defaultValue: c.driver }),
                mean: c.mean,
                suppressed: c.suppressed,
              }))}
              suppressedLabel={t('pulse.report.suppressed')}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ReportBars renders one section of labelled 1–5 bars. A suppressed row shows
// "not enough responses yet" with no bar — never a guessed value. The exact
// mean is always printed next to its bar so the bar length is only a visual aid.
function ReportBars({
  heading,
  rows,
  suppressedLabel,
}: {
  heading: string;
  rows: {
    key: string;
    label: string;
    mean: number | undefined;
    suppressed: boolean;
    // Optional trailing text shown instead of the numeric mean — used by the
    // participation view to print "10/15" while still drawing the bar.
    note?: string;
  }[];
  suppressedLabel: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-heading text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {heading}
      </span>
      <ul className="flex flex-col gap-1.5">
        {rows.map((row) => (
          <li key={row.key} className="flex items-center gap-3 text-sm">
            <span className="w-28 shrink-0 truncate">{row.label}</span>
            {row.suppressed || row.mean == null ? (
              <span className="text-xs text-muted-foreground">{suppressedLabel}</span>
            ) : (
              <>
                <span className="h-2 flex-1 overflow-hidden rounded-pill bg-muted">
                  <span
                    className="block h-full rounded-pill bg-primary"
                    style={{ width: `${Math.min(100, Math.max(0, (row.mean / 5) * 100))}%` }}
                  />
                </span>
                <span className="w-12 shrink-0 text-right font-medium tabular-nums">
                  {row.note ?? row.mean.toFixed(1)}
                </span>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// HeatmapGrid renders the team (or section) × driver grid — the "where do we
// look first?" view (§6). Each cell is a gated mean on the 1–5 scale, tinted by
// score; a suppressed cell shows a dash, never a guessed value. The grid scrolls
// horizontally on narrow screens rather than forcing the page to.
// heatColor maps a 1–5 mean to a diverging cell background: red below the
// midpoint (worse the lower it goes), lime above it. Uses the design tokens via
// color-mix so it tracks the theme; opacity is capped so the dark number stays
// readable on the strongest cells.
function heatColor(mean: number): string {
  if (mean < 3) {
    const s = (3 - mean) / 2; // 0 at 3.0 → 1 at 1.0
    return `color-mix(in srgb, var(--color-error) ${Math.round(20 + s * 60)}%, transparent)`;
  }
  const s = (mean - 3) / 2; // 0 at 3.0 → 1 at 5.0
  return `color-mix(in srgb, var(--lime) ${Math.round(15 + s * 70)}%, transparent)`;
}

function HeatmapGrid({ heatmap }: { heatmap: Heatmap }) {
  const { t } = useTranslation('common');
  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-heading text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {t('pulse.report.heatmap')}
      </span>
      <div className="overflow-x-auto">
        <table className="border-separate border-spacing-1 text-sm">
          <thead>
            <tr>
              <th className="pr-2 text-left text-xs font-medium text-muted-foreground">
                {t(`pulse.report.heatmapUnit.${heatmap.unit}`)}
              </th>
              {heatmap.drivers.map((d) => (
                <th
                  key={d}
                  className="px-1 text-center text-xs font-medium text-muted-foreground"
                >
                  {t(`pulse.drivers.${d}`, { defaultValue: d })}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {heatmap.rows.map((row) => (
              <tr key={row.unitId ?? row.unitLabel}>
                <td className="max-w-[10rem] truncate pr-2 font-medium">
                  {row.unitLabel || t('pulse.report.noSection')}
                </td>
                {row.cells.map((cell) => (
                  <td key={cell.driver} className="p-0">
                    {cell.suppressed || cell.mean == null ? (
                      <span
                        className="flex h-9 min-w-[3rem] items-center justify-center rounded-md bg-muted text-xs text-muted-foreground"
                        title={t('pulse.report.suppressed')}
                      >
                        –
                      </span>
                    ) : (
                      <span
                        className="flex h-9 min-w-[3rem] items-center justify-center rounded-md font-medium tabular-nums text-foreground"
                        style={{ backgroundColor: heatColor(cell.mean) }}
                        title={`${cell.mean.toFixed(1)} · n=${cell.n}`}
                      >
                        {cell.mean.toFixed(1)}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// SummaryPanel shows the AI theme summary of a run's free-text comments, and a
// button to (re)generate it. Generation is an async job — the button kicks it
// off (202), then the panel polls until the summary lands (the bell also
// notifies). The content is aggregate themes/risks/actions only, never a
// verbatim, rendered as escaped text (no HTML) so untrusted AI output is safe.
function SummaryPanel({
  slug,
  token,
  campaignId,
}: {
  slug: string;
  token: string | null;
  campaignId: string;
}) {
  const { t } = useTranslation('common');
  const qc = useQueryClient();
  const [generating, setGenerating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['pulse-summary', slug, campaignId],
    queryFn: () => getPulseSummary(token, slug, campaignId),
    refetchInterval: generating ? 4000 : false,
  });
  const summary = query.data ?? null;

  // Stop polling once the summary arrives.
  useEffect(() => {
    if (generating && summary) setGenerating(false);
  }, [generating, summary]);

  const genM = useMutation({
    mutationFn: () => summarizePulseCampaign(token, slug, campaignId),
    onSuccess: () => {
      setNotice(null);
      setGenerating(true);
      void qc.invalidateQueries({ queryKey: ['pulse-summary', slug, campaignId] });
    },
    onError: (e) => {
      setGenerating(false);
      if (e instanceof ApiError && e.status === 409) {
        setNotice(
          e.code === 'too_few_comments'
            ? t('pulse.summary.tooFew')
            : t('pulse.summary.unavailable'),
        );
      } else {
        setNotice(t('pulse.error'));
      }
    },
  });

  if (query.isLoading) {
    return <p className="text-sm text-muted-foreground">{t('pulse.report.loading')}</p>;
  }

  return (
    <div className="flex flex-col gap-2 rounded-xl bg-muted/40 p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 font-heading text-xs font-bold uppercase tracking-wider text-muted-foreground">
          <Sparkle size={14} weight="fill" aria-hidden />
          {t('pulse.summary.heading')}
        </span>
        <Button
          size="sm"
          variant="outline"
          disabled={genM.isPending || generating}
          onClick={() => genM.mutate()}
        >
          {summary ? t('pulse.summary.regenerate') : t('pulse.summary.generate')}
        </Button>
      </div>

      {generating && (
        <p className="text-sm text-muted-foreground">{t('pulse.summary.generating')}</p>
      )}
      {notice && <p className="text-sm text-muted-foreground">{notice}</p>}
      {!summary && !generating && !notice && (
        <p className="text-sm text-muted-foreground">{t('pulse.summary.empty')}</p>
      )}

      {summary && (
        <div className="flex flex-col gap-3">
          <SummaryList heading={t('pulse.summary.themes')} items={summary.themes} />
          <SummaryList heading={t('pulse.summary.risks')} items={summary.risks} />
          <SummaryList heading={t('pulse.summary.actions')} items={summary.actions} />
          <p className="text-xs text-muted-foreground">
            {t('pulse.summary.aiNote', { count: summary.commentCount })}
          </p>
        </div>
      )}
    </div>
  );
}

// SummaryList is one labelled bullet list (themes / risks / actions). Hidden
// when empty so a run with, say, no clear risks doesn't show an empty heading.
function SummaryList({ heading, items }: { heading: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-foreground/70">{heading}</span>
      <ul className="flex list-disc flex-col gap-0.5 pl-5 text-sm">
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    </div>
  );
}

// FollowupsPanel lists a run's anonymous follow-up threads. Each shows the
// anonymous comment it started from; expanding one loads the conversation and a
// reply box. The admin never learns who wrote it.
function FollowupsPanel({
  slug,
  token,
  campaignId,
}: {
  slug: string;
  token: string | null;
  campaignId: string;
}) {
  const { t } = useTranslation('common');
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['pulse-threads', slug, campaignId],
    queryFn: () => listPulseThreads(token, slug, campaignId),
  });

  if (query.isLoading || !query.data) {
    return (
      <p className="mt-3 border-t border-border pt-3 text-sm text-muted-foreground">
        {t('pulse.report.loading')}
      </p>
    );
  }
  const threads = query.data.threads;
  if (threads.length === 0) {
    return (
      <p className="mt-3 border-t border-border pt-3 text-sm text-muted-foreground">
        {t('pulse.followups.empty')}
      </p>
    );
  }

  return (
    <div className="mt-3 flex flex-col gap-2 border-t border-border pt-3">
      {threads.map((th: PulseThreadSummary) => (
        <div key={th.threadId} className="rounded-xl border border-border p-3">
          {th.comments.map((c, i) => (
            <p key={i} className="text-sm italic text-foreground/80">
              {c}
            </p>
          ))}
          <button
            type="button"
            className="mt-2 text-xs font-medium text-foreground/70 underline-offset-2 hover:underline"
            onClick={() =>
              setOpenThreadId((prev) => (prev === th.threadId ? null : th.threadId))
            }
          >
            {openThreadId === th.threadId
              ? t('pulse.followups.hide')
              : t('pulse.followups.openThread', { count: th.messageCount })}
          </button>
          {openThreadId === th.threadId && (
            <ThreadConversation slug={slug} token={token} threadId={th.threadId} />
          )}
        </div>
      ))}
    </div>
  );
}

// ThreadConversation shows one thread's messages and a reply box for the club.
function ThreadConversation({
  slug,
  token,
  threadId,
}: {
  slug: string;
  token: string | null;
  threadId: string;
}) {
  const { t } = useTranslation('common');
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const query = useQuery({
    queryKey: ['pulse-thread', slug, threadId],
    queryFn: () => getPulseThread(token, slug, threadId),
  });
  const reply = useMutation({
    mutationFn: (body: string) => replyPulseThread(token, slug, threadId, body),
    onSuccess: () => {
      setDraft('');
      void qc.invalidateQueries({ queryKey: ['pulse-thread', slug, threadId] });
    },
  });

  return (
    <div className="mt-2 flex flex-col gap-2 border-t border-border pt-2">
      {query.data?.messages.map((m, i) => (
        <div
          key={i}
          className={`max-w-[85%] rounded-xl px-3 py-1.5 text-sm ${
            m.sender === 'club'
              ? 'self-end bg-primary/15'
              : 'self-start bg-muted'
          }`}
        >
          {m.body}
        </div>
      ))}
      <div className="mt-1 flex items-end gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          placeholder={t('pulse.followups.replyPlaceholder')}
          className="min-h-[2.5rem] flex-1 resize-y rounded-xl border border-border bg-background p-2 text-sm"
        />
        <Button
          size="sm"
          disabled={draft.trim() === '' || reply.isPending}
          onClick={() => reply.mutate(draft.trim())}
        >
          <PaperPlaneTilt size={16} weight="bold" aria-hidden />
          {t('pulse.followups.send')}
        </Button>
      </div>
    </div>
  );
}

// SchedulesSection is the recurring auto-send surface: three schedules run by
// default (member / guardian / leader). It shows the next couple of runs per
// audience in one table; each row's ⋮ menu edits that audience's schedule (in a
// modal) or skips that single occurrence.
function SchedulesSection({ slug, token }: { slug: string; token: string | null }) {
  const { t } = useTranslation('common');
  const [editing, setEditing] = useState<PulseSchedule | null>(null);

  const query = useQuery({
    queryKey: ['pulse-schedules', slug],
    queryFn: () => listPulseSchedules(token, slug),
    enabled: !!slug,
  });
  const schedules = query.data?.schedules ?? [];

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border p-4">
      <div className="flex items-center gap-2">
        <CalendarDots size={18} weight="bold" aria-hidden />
        <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">
          {t('pulse.schedules.heading')}
        </h2>
      </div>
      <p className="max-w-2xl text-sm text-muted-foreground">
        {t('pulse.schedules.intro')}
      </p>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">{t('pulse.list.loading')}</p>
      ) : schedules.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
          {t('pulse.schedules.empty')}
        </p>
      ) : (
        <UpcomingTable slug={slug} token={token} schedules={schedules} onEdit={setEditing} />
      )}

      <ScheduleEditModal
        slug={slug}
        token={token}
        schedule={editing}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}

// UpcomingTable renders the next couple of runs for every enabled schedule as one
// proper table (like the members / teams lists): Date · Audience · ⋮ menu. The
// menu edits the audience's schedule or skips/restores that single run.
function UpcomingTable({
  slug,
  token,
  schedules,
  onEdit,
}: {
  slug: string;
  token: string | null;
  schedules: PulseSchedule[];
  onEdit: (s: PulseSchedule) => void;
}) {
  const { t, i18n } = useTranslation('common');
  const qc = useQueryClient();
  const updateM = useMutation({
    mutationFn: (v: { id: string; patch: Parameters<typeof updatePulseSchedule>[3] }) =>
      updatePulseSchedule(token, slug, v.id, v.patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['pulse-schedules', slug] }),
  });
  const fmtDate = (d: Date) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(d);

  // Enabled audiences contribute their next PER_CATEGORY occurrences (skipped
  // ones stay listed, marked, so they can be restored). PAUSED audiences get a
  // single dateless row so they stay visible and can be resumed/edited — without
  // it a paused schedule would vanish with no way back. Enabled rows sort by
  // date; paused rows sink to the bottom.
  const PER_CATEGORY = 2;
  type Row = { schedule: PulseSchedule; date: Date | null; skipped: boolean };
  const rows: Row[] = [];
  for (const s of schedules) {
    if (!s.enabled) {
      rows.push({ schedule: s, date: null, skipped: false });
      continue;
    }
    for (const occ of upcomingOccurrences(s.nextRunAt, s.intervalDays, PER_CATEGORY)) {
      rows.push({
        schedule: s,
        date: occ,
        skipped: s.skippedRuns.some((x) => sameUTCDay(new Date(x), occ)),
      });
    }
  }
  rows.sort((a, b) => {
    if (!a.date) return 1; // paused rows last
    if (!b.date) return -1;
    return a.date.getTime() - b.date.getTime();
  });
  if (rows.length === 0) return null;

  const toggleSkip = (row: Row) => {
    if (!row.date) return;
    const occ = row.date;
    const skipping = !row.skipped;
    const next = row.schedule.skippedRuns.filter((x) => !sameUTCDay(new Date(x), occ));
    if (skipping) next.push(occ.toISOString());
    updateM.mutate({ id: row.schedule.id, patch: { skippedRuns: next } });
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="font-heading text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {t('pulse.schedules.upcomingHeading')}
      </span>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>{t('pulse.schedules.colDate')}</TableHead>
              <TableHead>{t('pulse.schedules.colAudience')}</TableHead>
              <TableHead className="w-12 text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, i) => {
              const paused = row.date === null;
              return (
                <TableRow key={paused ? `paused-${row.schedule.id}` : row.schedule.id + row.date!.toISOString() + i}>
                  <TableCell
                    className={`whitespace-nowrap tabular-nums ${
                      paused || row.skipped
                        ? 'text-muted-foreground line-through'
                        : 'font-medium'
                    }`}
                  >
                    {paused ? (
                      <span className="no-underline">— {t('pulse.schedules.paused')}</span>
                    ) : (
                      <>
                        {fmtDate(row.date!)}
                        {row.skipped && (
                          <span className="ml-2 text-xs no-underline">
                            {t('pulse.schedules.skipped')}
                          </span>
                        )}
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={paused ? 'opacity-60' : ''}>
                      <RoleBadge role={row.schedule.targetRole} />
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-label={t('pulse.schedules.rowActions')}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-pill text-foreground/70 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <DotsThreeVertical size={18} weight="bold" aria-hidden />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => onEdit(row.schedule)}>
                          <PencilSimple size={16} weight="bold" aria-hidden />
                          {t('pulse.schedules.editSchedule')}
                        </DropdownMenuItem>
                        {paused ? (
                          <DropdownMenuItem
                            onSelect={() =>
                              updateM.mutate({ id: row.schedule.id, patch: { enabled: true } })
                            }
                          >
                            <SkipForward size={16} weight="bold" aria-hidden />
                            {t('pulse.schedules.resumeSchedule')}
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onSelect={() => toggleSkip(row)}
                            className={row.skipped ? '' : 'text-destructive focus:text-destructive'}
                          >
                            <SkipForward size={16} weight="bold" aria-hidden />
                            {row.skipped
                              ? t('pulse.schedules.resume')
                              : t('pulse.schedules.skipThis')}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ScheduleEditModal edits one audience's schedule in a modal (native <dialog>,
// matching the app's other edit modals): cadence + open window, pause/resume,
// and delete. Rendered once by SchedulesSection; opens whenever `schedule` is set.
function ScheduleEditModal({
  slug,
  token,
  schedule,
  onClose,
}: {
  slug: string;
  token: string | null;
  schedule: PulseSchedule | null;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');
  const qc = useQueryClient();
  const confirm = useConfirm();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [interval, setInterval] = useState(14);
  const [windowDays, setWindowDays] = useState(7);

  // Open/close the native dialog + seed the inputs whenever a schedule is picked.
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (schedule && !el.open) {
      setInterval(schedule.intervalDays);
      setWindowDays(schedule.windowDays);
      el.showModal();
    } else if (!schedule && el.open) {
      el.close();
    }
  }, [schedule]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const handleClose = () => onClose();
    el.addEventListener('close', handleClose);
    return () => el.removeEventListener('close', handleClose);
  }, [onClose]);

  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: ['pulse-schedules', slug] });
  const updateM = useMutation({
    mutationFn: (patch: Parameters<typeof updatePulseSchedule>[3]) =>
      updatePulseSchedule(token, slug, schedule!.id, patch),
    onSuccess: invalidate,
  });
  const deleteM = useMutation({
    mutationFn: () => deletePulseSchedule(token, slug, schedule!.id),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  // Always render the <dialog> so the ref exists for showModal; gate the content.
  if (!schedule) {
    return <dialog ref={dialogRef} className="hidden" aria-hidden />;
  }

  const dirty = interval !== schedule.intervalDays || windowDays !== schedule.windowDays;

  const onDelete = async () => {
    const ok = await confirm({
      title: t('pulse.schedules.deleteConfirmTitle'),
      body: t('pulse.schedules.deleteConfirmBody'),
      confirmLabel: t('pulse.schedules.delete'),
      tone: 'destructive',
      container: dialogRef.current,
    });
    if (ok) deleteM.mutate();
  };

  return (
    <dialog
      ref={dialogRef}
      className="rounded-2xl border border-border bg-background p-0 shadow-2xl backdrop:bg-foreground/40"
    >
      <div className="flex w-[min(28rem,92vw)] flex-col gap-4 p-6">
        <header className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1.5">
            <span className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('pulse.schedules.editSchedule')}
            </span>
            <span className="self-start">
              <RoleBadge role={schedule.targetRole} />
            </span>
          </div>
          <button
            type="button"
            aria-label={t('confirmDialog.cancel')}
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-pill text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X size={20} weight="bold" aria-hidden />
          </button>
        </header>

        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              {t('pulse.schedules.intervalLabel')}
            </span>
            <NumberField value={interval} onChange={setInterval} min={1} max={365} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              {t('pulse.schedules.windowLabel')}
            </span>
            <NumberField value={windowDays} onChange={setWindowDays} min={1} max={90} />
          </label>
        </div>

        {!schedule.enabled && (
          <p className="rounded-lg bg-muted p-2 text-xs text-muted-foreground">
            {t('pulse.schedules.paused')}
          </p>
        )}

        <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => updateM.mutate({ enabled: !schedule.enabled })}
              disabled={updateM.isPending}
            >
              {schedule.enabled ? t('pulse.schedules.pause') : t('pulse.schedules.resume')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void onDelete()}
              disabled={deleteM.isPending}
            >
              <Trash size={16} weight="bold" aria-hidden />
              {t('pulse.schedules.delete')}
            </Button>
          </div>
          <Button
            size="sm"
            onClick={() => updateM.mutate({ intervalDays: interval, windowDays })}
            disabled={!dirty || updateM.isPending}
          >
            {t('pulse.schedules.save')}
          </Button>
        </footer>
      </div>
    </dialog>
  );
}

// upcomingOccurrences projects the next n occurrences from an anchor instant,
// stepping by intervalDays in UTC calendar days so the dates line up with the
// worker's UTC day-based skip matching.
function upcomingOccurrences(anchorISO: string, intervalDays: number, n: number): Date[] {
  const base = new Date(anchorISO);
  const out: Date[] = [];
  for (let i = 0; i < n; i++) {
    out.push(
      new Date(
        Date.UTC(
          base.getUTCFullYear(),
          base.getUTCMonth(),
          base.getUTCDate() + i * intervalDays,
          base.getUTCHours(),
          base.getUTCMinutes(),
        ),
      ),
    );
  }
  return out;
}

// sameUTCDay reports whether two instants fall on the same UTC calendar day —
// mirrors the backend's day-granular skip match.
function sameUTCDay(a: Date, b: Date): boolean {
  return (
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate()
  );
}

// NumberField is a small numeric input for the day-count cadence fields. Native
// number input is acceptable here (it's neither a date, time, nor select).
function NumberField({
  value,
  onChange,
  min,
  max,
}: {
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
}) {
  return (
    <input
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      value={value}
      onChange={(e) => {
        const n = Number(e.target.value);
        if (!Number.isNaN(n)) onChange(Math.max(min, Math.min(max, n)));
      }}
      className="w-20 rounded-lg border border-border bg-background px-2 py-1.5 text-sm"
    />
  );
}
